<template>
    <article>
        <v-tabs v-model="tab" height="40" slider-size="2" show-arrows class="mb-4">
            <v-tab :to="{hash: 'ce'}" tab-value="ce">Community Edition</v-tab>
            <v-tab :to="{hash: 'ee'}" tab-value="ee">Enterprise Edition</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
            <v-tab-item value="ce" eager transition="none">
                <h3>Step #1: Install Docker Compose (if not installed)</h3>

                <p>Use the following commands to install Docker Compose on Ubuntu:</p>

                <CodeSnippet>
                    <pre v-hl.bash>
apt update
apt install docker-compose-v2
                    </pre>
                </CodeSnippet>

                <h3>Step #2: Deploy Coroot</h3>

                <p>
                    To deploy Coroot using Docker Compose, run the following command.
                    Before applying it, you can review the configuration file in Coroot's GitHub repository:
                    <a href="https://github.com/coroot/coroot/blob/main/deploy/docker-compose.yaml" target="_blank">docker-compose.yaml</a>
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -fsS https://raw.githubusercontent.com/coroot/coroot/main/deploy/docker-compose.yaml | docker compose -f - up -d
                    </pre>
                </CodeSnippet>

                <h3>Step #3: Validate the deployment</h3>

                <p>
                    Ensure that the Coroot containers are running by executing the following command:
                </p>

                <CodeSnippet>
                    <pre v-hl.bash>
docker ps
                    </pre>
                </CodeSnippet>

                <p>
                    You should see an output similar to this if the deployment is successful:
                </p>

                <pre v-hl.bash>
CONTAINER ID   IMAGE                               COMMAND                  CREATED          STATUS          PORTS                                          NAMES
9451a61a8136   ghcr.io/coroot/coroot:latest        "/opt/coroot/coroot …"   33 seconds ago   Up 31 seconds   0.0.0.0:8080->8080/tcp, :::8080->8080/tcp      root-coroot-1
e81fdd93cffc   clickhouse/clickhouse-server:24.3   "/entrypoint.sh"         33 seconds ago   Up 32 seconds   8123/tcp, 9009/tcp, 127.0.0.1:9000->9000/tcp   root-clickhouse-1
9e8b809db618   prom/prometheus:v2.45.4             "/bin/prometheus --c…"   33 seconds ago   Up 32 seconds   127.0.0.1:9090->9090/tcp                       root-prometheus-1
6431fb47f21a   ghcr.io/coroot/coroot-node-agent    "coroot-node-agent -…"   3 hours ago      Up 32 seconds                                                  root-coroot-node-agent-1
                </pre>

                <h3>Step #4: Accessing Coroot</h3>

                <ul>
                    <li>
                        If you installed Coroot on your desktop machine, you can access it at <a href="http://localhost:8080/" target="_blank">http://localhost:8080/</a>.
                    </li>
                    <li>
                        If Coroot is deployed on a remote node, replace &lt;NODE_IP_ADDRESS&gt; with the IP address of the node in the following URL: http://&lt;NODE_IP_ADDRESS&gt;:8080/.
                    </li>
                </ul>

                <h2>Uninstall Coroot</h2>

                <p>To uninstall Coroot run the following command:</p>

                <CodeSnippet>
                <pre v-hl.bash>
curl -fsS https://raw.githubusercontent.com/coroot/coroot/main/deploy/docker-compose.yaml | docker compose rm -f -s -v
                </pre>
                </CodeSnippet>

            </v-tab-item>

            <v-tab-item value="ee" eager transition="none">
                <v-alert color="info" outlined text class="mt-5">
                    The Enterprise Edition is a paid subscription (from $1 per CPU core/month) that offers extra features and priority support.
                    <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
                </v-alert>

                <h3>Step #1: Install Docker Compose (if not installed)</h3>

                <p>Use the following commands to install Docker Compose on Ubuntu:</p>

                <CodeSnippet>
                    <pre v-hl.bash>
apt update
apt install docker-compose-v2
                    </pre>
                </CodeSnippet>

                <h3>Step #2: Deploy Coroot</h3>

                <v-alert color="info" outlined text class="mt-5">
                    To install Coroot Enterprise Edition, you'll need a license (from $1 per CPU core/month).
                    <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
                </v-alert>

                <div class="subtitle-1">License key:</div>
                <v-form>
                    <v-text-field
                        v-model="license_key"
                        placeholder="COROOT-11111111-1111-1111-1111-111111111111"
                        outlined
                        dense
                    />
                </v-form>

                <p>
                    To deploy Coroot using Docker Compose, run the following command.
                    Before applying it, you can review the configuration file in Coroot's GitHub repository:
                    <a href="https://github.com/coroot/coroot/blob/main/deploy/docker-compose.yaml" target="_blank">docker-compose.yaml</a>
                </p>

                <CodeSnippet>
                    <pre class="hl">
curl -fsS https://raw.githubusercontent.com/coroot/coroot-ee/main/deploy/docker-compose.yaml | \
    LICENSE_KEY="{{license_key || 'COROOT-LICENSE-KEY-HERE'}}" docker compose -f - up -d</pre>
                </CodeSnippet>

                <h3>Step #3: Validate the deployment</h3>

                <p>
                    Ensure that the Coroot containers are running by executing the following command:
                </p>

                <CodeSnippet>
                    <pre v-hl.bash>
docker ps
                    </pre>
                </CodeSnippet>

                <p>
                    You should see an output similar to this if the deployment is successful:
                </p>

                <pre v-hl.bash>
CONTAINER ID   IMAGE                                 COMMAND                  CREATED              STATUS              PORTS                                          NAMES
870119cb6859   ghcr.io/coroot/coroot-cluster-agent   "coroot-cluster-agen…"   29 seconds ago       Up 16 seconds                                                      coroot-ee-cluster-agent-1
6f3b8f1c821c   ghcr.io/coroot/coroot-ee:1.5.4        "/opt/coroot/coroot-…"   42 seconds ago       Up 16 seconds       0.0.0.0:8080->8080/tcp, :::8080->8080/tcp      coroot-ee-coroot-1
320e9154a8ba   clickhouse/clickhouse-server:24.3     "/entrypoint.sh"         About a minute ago   Up About a minute   8123/tcp, 9009/tcp, 127.0.0.1:9000->9000/tcp   coroot-ee-clickhouse-1
76b5968068f0   prom/prometheus:v2.45.4               "/bin/prometheus --c…"   About a minute ago   Up About a minute   127.0.0.1:9090->9090/tcp                       coroot-ee-prometheus-1
51e91e09e58a   ghcr.io/coroot/coroot-node-agent      "coroot-node-agent -…"   About a minute ago   Up About a minute                                                  coroot-ee-node-agent-1
                </pre>

                <h3>Step #4: Accessing Coroot</h3>

                <ul>
                    <li>
                        If you installed Coroot on your desktop machine, you can access it at <a href="http://localhost:8080/" target="_blank">http://localhost:8080/</a>.
                    </li>
                    <li>
                        If Coroot is deployed on a remote node, replace &lt;NODE_IP_ADDRESS&gt; with the IP address of the node in the following URL: http://&lt;NODE_IP_ADDRESS&gt;:8080/.
                    </li>
                </ul>

                <h2>Uninstall Coroot</h2>

                <p>To uninstall Coroot run the following command:</p>

                <CodeSnippet>
                <pre v-hl.bash>
curl -fsS https://raw.githubusercontent.com/coroot/coroot-ee/main/deploy/docker-compose.yaml | docker compose rm -f -s -v
                </pre>
                </CodeSnippet>

            </v-tab-item>
        </v-tabs-items>
    </article>
</template>

<script>

export default {
    data() {
        return {
            license_key: '',
            tab: null,
        }
    },
    computed: {},
}
</script>

<style scoped>
</style>
